import dayjs from "dayjs";
import { Draft } from "./Draft.util";

import { IUser } from "@models/user.model";
import { IPhoneNumber } from "@models/PhoneNumber";
import { ISubmission } from "@models/Submission.model";
import {
  IContactAndBilling,
  IEventRegistrationForm,
} from "@models/EventRegistrationForm.model";
import { IDemoRegistrationForm } from "@models/DemoRegistrationForm.model";

import COUNTRY_CODES from "@components/PhoneNumberInput/jsons/COUNTRY_CODES.json";
import { ISubmissionConditions } from "@models/SubmissionConditions.model";
import { IIVolunteeringFormData } from "@routes/VolunteeringFormRoute/VolunteeringFormRoute.types";

const COUNTRY_CODES_OBJ = COUNTRY_CODES.reduce(
  (pv, cv) => ({ ...pv, [cv.code]: cv.dial_code }),
  {} as { [CODE: string]: string }
);

export class Exporter {
  static BASIC_INFORMATION_COLUMNS = [
    "Vorname",
    "Nachname",
    "E-Mail-Adresse",
    "Rufnummer",
  ];
  static CONTACT_AND_BILLING_COLUMNS = [
    "Anmeldende",
    "Name",
    "Kontaktperson Vorname",
    "Kontaktperson Nachname",
    "Straße und Hausnummer",
    "PLZ",
    "Stadt",
    "Land",
    "Rufnummer",
    "die Rechnungsanschrift ist identisch der Anmelden Person/Organisation/Gruppe",
    "Anmeldende",
    "Ggf. Zusatz auf Rechnung",
    "Zusatz auf Rechnung",
    "Ggf. Kontaktperson auf Rechnung",
    "Kontaktperson auf Rechnung",
    "Straße und Hausnummer",
    "PLZ",
    "Stadt",
    "Land",
    "Rufnummer",
  ];

  static SUBMISSION_CONDITIONS_COLUMNS = [
    "Datenschutz akzeptiert",
    "Teilnahmebedingungen akzeptiert",
    "Wir dürfen deine Daten für weitere Veranstaltungen speichern",
  ];

  static getSubmissionConditionsColumnsNames = (
    hideTermsAndConditions?: boolean
  ) =>
    Exporter.SUBMISSION_CONDITIONS_COLUMNS.filter((_, i) => {
      if (hideTermsAndConditions && i === 1) return false;
      return true;
    });

  static parsePhone(phone: Draft<IPhoneNumber>) {
    const { code = "", number = "" } = phone;
    return `${code ? COUNTRY_CODES_OBJ[code] : ""} ${number || ""}`;
  }

  static exportBasicInformation(user: IUser) {
    const nRow = [];
    // basic information
    {
      const {
        firstName,
        lastName,
        email,
        phone: { code, number },
      } = user;
      nRow.push(firstName);
      nRow.push(lastName);
      nRow.push(email);
      nRow.push(`${COUNTRY_CODES_OBJ[code] || ""} ${number}`);
    }

    return nRow;
  }

  static exportContactAndBilling(draft: Draft<IContactAndBilling>) {
    const nRow: string[] = [];
    // address
    const parsedType = {
      person: "Person",
      organization: "Organisation",
      company: "Firma",
      group: "Gruppe",
    };
    {
      const {
        address: {
          type,
          streetAndHouse,
          postCode,
          city,
          country,
          phone = {} as NonNullable<
            NonNullable<(typeof draft)["address"]>["phone"]
          >,
        } = {} as NonNullable<(typeof draft)["address"]>,
        name,
        firstName,
        lastName,
        isBillingAddressSame,
      } = draft;
      nRow.push(type ? parsedType[type as keyof typeof parsedType] || "" : "");
      nRow.push(name || "");
      nRow.push(firstName || "");
      nRow.push(lastName || "");
      nRow.push(streetAndHouse || "");
      nRow.push(postCode || "");
      nRow.push(city || "");
      nRow.push(country || "");
      nRow.push(Exporter.parsePhone(phone));
      nRow.push(isBillingAddressSame ? "ja" : "nein");
    }
    // billing address
    {
      const {
        isBillingAddressSame,
        billingAddress: {
          type,
          streetAndHouse,
          postCode,
          city,
          country,
          options,
          phone = {} as NonNullable<
            NonNullable<(typeof draft)["billingAddress"]>["phone"]
          >,
          additionToInvoice,
          contactPersonOnInvoice,
        } = {} as NonNullable<(typeof draft)["billingAddress"]>,
      } = draft;
      if (isBillingAddressSame) nRow.push(...Array(10).fill(""));
      else {
        nRow.push(
          type ? parsedType[type as keyof typeof parsedType] || "" : ""
        );
        nRow.push(options?.includes("addition") ? "ja" : "nein");
        nRow.push(additionToInvoice || "");
        nRow.push(options?.includes("contact") ? "ja" : "nein");
        nRow.push(contactPersonOnInvoice || "");
        nRow.push(streetAndHouse || "");
        nRow.push(postCode || "");
        nRow.push(city || "");
        nRow.push(country || "");
        nRow.push(Exporter.parsePhone(phone));
      }
    }

    return nRow;
  }

  static exportSubmissionConditions(
    draft: Draft<ISubmissionConditions>,
    hideTermsAndConditions?: boolean
  ) {
    // submissions conditions
    const scRow: string[] = [];
    const { conditionsAccepted } = draft;

    scRow.push(
      conditionsAccepted?.includes("privaryPolicyAccepted") ? "ja" : "nein"
    );

    if (!hideTermsAndConditions)
      scRow.push(
        conditionsAccepted?.includes("conditionsOfParticipationAccepted")
          ? "ja"
          : "nein"
      );

    scRow.push(
      conditionsAccepted?.includes("saveDataForFutureEventsAccepted")
        ? "ja"
        : "nein"
    );

    return scRow;
  }

  static exportVolunteeringSubmissions(
    submissions: ISubmission<IIVolunteeringFormData>[]
  ) {
    const columns: string[] = [];

    columns.push(...Exporter.BASIC_INFORMATION_COLUMNS);

    columns.push(
      ...[
        "Vorname",
        "Nachname",
        "Anrede",
        "Rufnummer",
        "E-Mail-Adresse",
        "Geburtsdatum (MM.DD.YY)",
        "Adresse",
        "PLZ",
        "Stadt",
      ]
    );

    columns.push(...["Demoengel", "Demoteam"]);

    columns.push(
      ...[
        "Aufbauhelfer:in Donnerstag",
        "Aufbauhelfer:in Freitag",
        "Aufbauhelfer:in Sonntag",
        "Aufbauhelfer:in Montag",
      ]
    );

    columns.push(
      ...[
        "Backstagehelfer:in Freitag",
        "Backstagehelfer:in Samstag",
        "Backstagehelfer:in Sonntag",
      ]
    );

    columns.push(
      ...[
        "Meetingpointhelfer:in Freitag",
        "Meetingpointhelfer:in Samstag",
        "Meetingpointhelfer:in Sonntag",
      ]
    );

    columns.push(
      ...[
        "Pridebändchen-Verteiler:in Freitag",
        "Pridebändchen-Verteiler:in Samstag",
        "Pridebändchen-Verteiler:in Sonntag",
        "Pridebändchen-Verteiler:in Demo - Sonntag",
      ]
    );

    columns.push(...Exporter.getSubmissionConditionsColumnsNames(true));

    return [
      columns,
      ...submissions.map<string[]>((submission) => {
        const nRow: string[] = [];
        nRow.push(...Exporter.exportBasicInformation(submission.user));

        const {
          form: {
            firstName,
            lastName,
            salutation,
            phone = {},
            email,
            dob,
            address,
            postCode,
            city,
            demoAngel,
            demoTeam,
            constructionHelper,
            backstageHelper,
            meetingPointHelper,
            prideBraceletDistributer,
          } = {} as NonNullable<(typeof submission)["form"]>,
        } = submission;

        nRow.push(firstName || "");
        nRow.push(lastName || "");
        nRow.push(salutation || "");
        nRow.push(Exporter.parsePhone(phone));
        nRow.push(email || "");
        nRow.push(dob ? dayjs(dob).format("MM.DD.YY") : "");
        nRow.push(address || "");
        nRow.push(postCode || "");
        nRow.push(city || "");

        nRow.push(demoAngel ? "ja" : "nein");
        nRow.push(demoTeam ? "ja" : "nein");

        nRow.push(
          constructionHelper?.includes("7/6/23 12:00-18:00") ? "ja" : "nein"
        );
        nRow.push(
          constructionHelper?.includes("7/7/23 10:00-14:00") ? "ja" : "nein"
        );
        nRow.push(
          constructionHelper?.includes("7/9/23 22:00-00:00") ? "ja" : "nein"
        );
        nRow.push(
          constructionHelper?.includes("7/10/23 11:00-15:00") ? "ja" : "nein"
        );

        nRow.push(
          backstageHelper?.includes("7/7/23 15:00-23:30") ? "ja" : "nein"
        );
        nRow.push(
          backstageHelper?.includes("7/8/23 12:00-23:30") ? "ja" : "nein"
        );
        nRow.push(
          backstageHelper?.includes("7/9/23 11:00-22:30") ? "ja" : "nein"
        );

        nRow.push(
          meetingPointHelper?.includes("7/7/23 15:00-23:30") ? "ja" : "nein"
        );
        nRow.push(
          meetingPointHelper?.includes("7/8/23 12:00-23:30") ? "ja" : "nein"
        );
        nRow.push(
          meetingPointHelper?.includes("7/9/23 11:00-22:30") ? "ja" : "nein"
        );

        nRow.push(
          prideBraceletDistributer?.includes("7/7/23 18:00-22:30")
            ? "ja"
            : "nein"
        );
        nRow.push(
          prideBraceletDistributer?.includes("7/8/23 15:00-23:00")
            ? "ja"
            : "nein"
        );
        nRow.push(
          prideBraceletDistributer?.includes("7/9/23 15:00-21:30")
            ? "ja"
            : "nein"
        );
        nRow.push(
          prideBraceletDistributer?.includes("7/9/23 10:00-15:00")
            ? "ja"
            : "nein"
        );

        const scRow = Exporter.exportSubmissionConditions(
          submission.form || {},
          true
        );
        return [...nRow, ...scRow];
      }),
    ];
  }

  static exportEventRegistrationSubmissions(
    submissions: ISubmission<IEventRegistrationForm>[]
  ) {
    const columns: string[] = [];
    columns.push(...Exporter.BASIC_INFORMATION_COLUMNS);
    columns.push(...Exporter.CONTACT_AND_BILLING_COLUMNS);

    columns.push(
      ...[
        "Titel der Veranstaltung",
        "Datum der Veranstaltung (MM.DD.YY)",
        "Beginn der Veranstaltung (HH:mm)",
        "Ende der Veranstaltung (HH:mm)",
        "Verantstaltungsbeschreibung",
        "URL",
        "Angabe zum Fotocredit",
        "Angaben zur Barrierefreiheit",
      ]
    );

    columns.push(...Exporter.getSubmissionConditionsColumnsNames());

    return submissions.reduce(
      (pvRows, submission) => {
        const nRow: string[] = [];

        nRow.push(...Exporter.exportBasicInformation(submission.user));
        // step 1
        nRow.push(...Exporter.exportContactAndBilling(submission.form || {}));

        const events = submission.form?.events || [{}];
        if (!events.length) events.push({});

        const nRows = events.map((event): string[] => {
          const row: string[] = [...nRow];

          const parsedAccessibility = {
            physical: "Menschen mit körperlicher Behinderung",
            hearing: "Menschen mit Hörbehinderung",
            visual: "Menschen mit Sehbehinderung",
          };

          const {
            title,
            date,
            time,
            endTime,
            description,
            image,
            credit,
            accessibility,
          } = event || ({} as NonNullable<typeof event>);

          row.push(title || "");
          row.push(date ? dayjs(date).format("MM.DD.YY") : "");
          row.push(time ? dayjs(time).format("HH:mm") : "");
          row.push(endTime ? dayjs(endTime).format("HH:mm") : "");
          row.push(
            (description || "").replaceAll("\n", "").replaceAll('"', '""') || ""
          );
          row.push(image || "");
          row.push(credit || "");
          row.push(
            (accessibility || [])
              .map(
                (v) =>
                  parsedAccessibility[v as keyof typeof parsedAccessibility]
              )
              .join(" / ")
          );

          return row;
        });

        const scRow = Exporter.exportSubmissionConditions(
          submission.form || {}
        );

        return [
          ...pvRows,
          ...(nRows?.length
            ? nRows.map((row) => [...row, ...scRow])
            : [[...nRow, ...scRow]]),
        ];
      },
      [columns] as string[][]
    );
  }

  static exportDemoRegistrationSubmissions(
    submissions: ISubmission<IDemoRegistrationForm>[]
  ) {
    // basic information
    const columns = [...Exporter.BASIC_INFORMATION_COLUMNS];
    // step 1
    columns.push(...Exporter.CONTACT_AND_BILLING_COLUMNS);
    // demo information
    columns.push(
      ...[
        "Gruppenname bei der Demo",
        "Vorstellung Deiner Gruppe und Umsetzung des Mottos",
        "Sonstige",
        "Personen an",
      ]
    );
    // vehicles information
    columns.push(
      ...[
        "Fahrzeug",
        "Fahrzeugtyp",
        "Personenbeförderung auf der Ladefläche",
        "Gesamtlänge (in meter)",
        "Gesamthöhe (in meter)",
        "Gesamtbreite (in meter)",
        "Gesamtgewicht (in kg)",
        "Kennzeichen",
        "Kennzeichen Anhänger identisch",
        "Kennzeichen Anhänger",
        // primaryVehicleRepresentative
        "Wagenleiter:in Name",
        "Wagenleiter:in Rufnummer",
        "Wagenleiter:in E-Mail",
        // secondaryVehicleRepresentative
        "Wagenleiter:in Vertreter:in Name",
        "Wagenleiter:in Vertreter:in Rufnummer",
        "Wagenleiter:in Vertreter:in E-Mail",
      ]
    );

    // submissions conditions
    columns.push(...Exporter.getSubmissionConditionsColumnsNames(true));

    const rows = submissions.reduce(
      (pvRows, submission) => {
        const nRow: string[] = [];

        nRow.push(...Exporter.exportBasicInformation(submission.user));
        // step 1
        nRow.push(...Exporter.exportContactAndBilling(submission.form || {}));
        // demo information
        {
          const {
            form: {
              group: { name, description, comments, people } = {} as Draft<
                IDemoRegistrationForm["group"]
              >,
            } = {} as IDemoRegistrationForm,
          } = submission;
          nRow.push(name || "");
          nRow.push(
            description?.replaceAll("\n", "").replaceAll('"', '""') || ""
          );
          nRow.push(comments || "");
          nRow.push(people?.toString() || "");
        }

        const scRow = Exporter.exportSubmissionConditions(
          submission.form || {},
          true
        );

        const vehicles = submission.form?.vehicles || [{}];
        if (!vehicles.length) vehicles.push({});

        const nRows = vehicles.map((vehicle) => {
          const row = [...nRow];

          const parsedType = {
            car: "PKW",
            "truckUpto7.5t": "LKW bis 7,5t",
            "truckOver7.5t": "LKW über 7,5t",
            tractorWithTrailer: "Zugmaschine mit Auflieger",
          };

          const {
            categories,
            type,
            passengerTransport,
            length,
            height,
            width,
            weight,
            plateNumber,
            isTrailerPlateNumberIdentical,
            trailerPlateNumber,
            primaryVehicleRepresentative = {} as Draft<
              IDemoRegistrationForm["vehicles"][number]["primaryVehicleRepresentative"]
            >,
            secondaryVehicleRepresentative = {} as Draft<
              IDemoRegistrationForm["vehicles"][number]["secondaryVehicleRepresentative"]
            >,
          } = vehicle ||
          ({} as Draft<IDemoRegistrationForm["vehicles"][number]>);

          row.push(categories ? `Gruppenkategorie ${categories}` : "");
          row.push(type ? parsedType[type as keyof typeof parsedType] : "");
          row.push(
            passengerTransport
              ? passengerTransport === "yes"
                ? "ja"
                : "nein"
              : ""
          );
          row.push(length?.toString() || "");
          row.push(height?.toString() || "");
          row.push(width?.toString() || "");
          row.push(weight?.toString() || "");
          row.push(plateNumber || "");
          row.push(
            isTrailerPlateNumberIdentical
              ? isTrailerPlateNumberIdentical === "yes"
                ? "ja"
                : "nein"
              : ""
          );
          row.push(
            isTrailerPlateNumberIdentical === "yes"
              ? ""
              : trailerPlateNumber || ""
          );

          row.push(primaryVehicleRepresentative.name || "");
          row.push(
            Exporter.parsePhone(primaryVehicleRepresentative.phone || {})
          );
          row.push(primaryVehicleRepresentative.email || "");

          row.push(secondaryVehicleRepresentative.name || "");
          row.push(
            Exporter.parsePhone(secondaryVehicleRepresentative.phone || {})
          );
          row.push(secondaryVehicleRepresentative.email || "");

          return row;
        });

        return [
          ...pvRows,
          ...(nRows?.length
            ? nRows.map((row) => [...row, ...scRow])
            : [[...nRow, ...scRow]]),
        ];
      },
      [columns] as string[][]
    );
    return rows;
  }

  private static arrayToCSV(arr: string[][], delimiter = ",") {
    return arr.map((v) => v.map((x) => `"${x}"`).join(delimiter)).join("\n");
  }
  static download(arr: string[][], name: string) {
    const csv = Exporter.arrayToCSV(arr);
    const hiddenElement = document.createElement("a");
    hiddenElement.href =
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    hiddenElement.target = "_blank";

    //provide the name for the CSV file to be downloaded
    hiddenElement.download = `${name}.csv`;
    hiddenElement.click();
    hiddenElement.remove();
  }
}
